import { useEffect, useState } from 'react'
import api from '../services/api'

// Tabela de preços do posto (GeneXus TPRAVAL → frota.praval), aba do Cadastro de Postos.
// Cada reajuste é uma linha por data; o formulário já abre com os preços atuais, só muda o que subiu/desceu.
interface Linha { pradat: string; prades: number; pratipdes: string; [col: string]: number | string }

const COMBUSTIVEIS: { nome: string; comum: string; aditivado?: string }[] = [
  { nome: 'Diesel S-10', comum: 'praga0', aditivado: 'praga3' },
  { nome: 'Diesel S-500', comum: 'pranum', aditivado: 'pranu2' },
  { nome: 'Diesel S-50', comum: 'pranu1', aditivado: 'pranu3' },
  { nome: 'Gasolina', comum: 'pragas', aditivado: 'praga2' },
  { nome: 'Etanol', comum: 'praalc', aditivado: 'praal2' },
  { nome: 'ARLA 32', comum: 'praga4' },
  { nome: 'GNV', comum: 'praga1' },
]
const COLUNAS = COMBUSTIVEIS.flatMap(c => (c.aditivado ? [c.comum, c.aditivado] : [c.comum]))

const hojeISO = () => new Date(Date.now() - 3 * 3600 * 1000).toISOString().slice(0, 10)
const dataBR = (d: string) => d.split('-').reverse().join('/')
const fmt = (n: number, casas = 4) => n.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
const parse = (s: string) => Number(String(s).replace(/\./g, '').replace(',', '.')) || 0

function Variacao({ atual, novo }: { atual: number; novo: number }) {
  if (!atual || !novo || Math.abs(novo - atual) < 0.00005) return <span style={{ color: '#9ca3af' }}>—</span>
  const dif = novo - atual
  const cor = dif > 0 ? '#b91c1c' : '#047857'
  return <span style={{ color: cor, fontWeight: 600 }}>{dif > 0 ? '▲' : '▼'} {fmt(Math.abs(dif))} ({fmt((dif / atual) * 100, 1)}%)</span>
}

export default function TabelaPrecosPosto({ pracod }: { pracod: string }) {
  const [historico, setHistorico] = useState<Linha[]>([])
  const [data, setData] = useState(hojeISO())
  const [valores, setValores] = useState<Record<string, string>>({})
  const [desconto, setDesconto] = useState('0,00')
  const [tipo, setTipo] = useState('T')
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ texto: string; ok: boolean } | null>(null)

  const existente = historico.find(h => h.pradat === data)
  // Base de comparação: o preço vigente antes da data do formulário.
  const anterior = historico.find(h => h.pradat < data)

  function preencher(l: Linha | undefined, novaData: string) {
    setData(novaData)
    setValores(Object.fromEntries(COLUNAS.map(c => [c, fmt(Number(l?.[c]) || 0)])))
    setDesconto(fmt(Number(l?.prades) || 0, 2))
    setTipo(String(l?.pratipdes || 'T'))
  }

  async function carregar(dataForm?: string) {
    const lista: Linha[] = (await api.get(`/posto-cadastro/${encodeURIComponent(pracod)}/precos`)).data
    setHistorico(lista)
    const d = dataForm || hojeISO()
    preencher(lista.find(h => h.pradat === d) || lista.find(h => h.pradat < d), d)
  }
  useEffect(() => { setMsg(null); carregar() }, [pracod])

  function trocarData(d: string) {
    if (!d) return setData(d)
    preencher(historico.find(h => h.pradat === d) || historico.find(h => h.pradat < d), d)
  }

  async function gravar() {
    setSalvando(true); setMsg(null)
    try {
      const corpo = { prades: parse(desconto), pratipdes: tipo, ...Object.fromEntries(COLUNAS.map(c => [c, parse(valores[c] || '0')])) }
      const r = await api.put(`/posto-cadastro/${encodeURIComponent(pracod)}/precos/${data}`, corpo)
      await carregar(data)
      setMsg({ texto: r.data.novo ? `Preços de ${dataBR(data)} gravados.` : `Preços de ${dataBR(data)} corrigidos.`, ok: true })
    } catch (e: any) { setMsg({ texto: e?.response?.data?.error || 'Erro ao gravar.', ok: false }) }
    finally { setSalvando(false) }
  }

  async function excluir(d: string) {
    if (!window.confirm(`Excluir a tabela de preços de ${dataBR(d)}?`)) return
    setMsg(null)
    try { await api.delete(`/posto-cadastro/${encodeURIComponent(pracod)}/precos/${d}`); await carregar() }
    catch (e: any) { setMsg({ texto: e?.response?.data?.error || 'Erro ao excluir.', ok: false }) }
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }
  const td: React.CSSProperties = { padding: '5px 8px', fontSize: '0.82rem', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }
  const inputNum: React.CSSProperties = { width: 100, textAlign: 'right' }

  return (
    <div>
      <div style={{ background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8', borderRadius: '0.5rem', padding: '0.7rem 0.85rem', marginBottom: '0.8rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.9rem', alignItems: 'flex-end', marginBottom: '0.6rem' }}>
          <div className="field-cad" style={{ width: 150 }}>
            <label>Vigente a partir de</label>
            <input type="date" max={hojeISO()} value={data} onChange={e => trocarData(e.target.value)} style={{ width: '100%' }} />
          </div>
          <div className="field-cad" style={{ width: 100 }}>
            <label>Desconto %</label>
            <input type="text" inputMode="decimal" value={desconto} onChange={e => setDesconto(e.target.value)} style={{ width: '100%', textAlign: 'right' }} />
          </div>
          <div className="field-cad" style={{ width: 170 }}>
            <label>Desconto aplicado em</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ width: '100%' }}>
              <option value="T">Todas as despesas</option>
              <option value="C">Só combustível</option>
            </select>
          </div>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: existente ? '#b45309' : '#1a6ea8', paddingBottom: 6 }}>
            {existente ? `Já existe tabela em ${dataBR(data)} — gravar vai corrigir essa data` : anterior ? `Novo reajuste (atual desde ${dataBR(anterior.pradat)})` : 'Primeira tabela de preços do posto'}
          </span>
        </div>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Combustível</th>
              <th style={{ ...th, textAlign: 'right' }}>Comum</th>
              <th style={th}>Variação</th>
              <th style={{ ...th, textAlign: 'right' }}>Aditivado</th>
              <th style={th}>Variação</th>
            </tr>
          </thead>
          <tbody>
            {COMBUSTIVEIS.map(c => (
              <tr key={c.comum}>
                <td style={{ ...td, fontWeight: 600 }}>{c.nome}</td>
                <td style={td}><input type="text" inputMode="decimal" value={valores[c.comum] ?? ''} onChange={e => setValores(v => ({ ...v, [c.comum]: e.target.value }))} onFocus={e => e.target.select()} style={inputNum} /></td>
                <td style={{ ...td, fontSize: '0.75rem', minWidth: 120 }}><Variacao atual={Number(anterior?.[c.comum]) || 0} novo={parse(valores[c.comum] || '0')} /></td>
                {c.aditivado ? (
                  <>
                    <td style={td}><input type="text" inputMode="decimal" value={valores[c.aditivado] ?? ''} onChange={e => setValores(v => ({ ...v, [c.aditivado!]: e.target.value }))} onFocus={e => e.target.select()} style={inputNum} /></td>
                    <td style={{ ...td, fontSize: '0.75rem', minWidth: 120 }}><Variacao atual={Number(anterior?.[c.aditivado]) || 0} novo={parse(valores[c.aditivado] || '0')} /></td>
                  </>
                ) : <><td style={td} /><td style={td} /></>}
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: '0.7rem' }}>
          <button className="btn btn-primary" onClick={gravar} disabled={salvando || !data}>{salvando ? 'Gravando...' : existente ? 'Corrigir preços' : 'Gravar reajuste'}</button>
          {msg && <span style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6, background: msg.ok ? '#d1fae5' : '#fee2e2', color: msg.ok ? '#065f46' : '#991b1b' }}>{msg.texto}</span>}
        </div>
      </div>

      <h4 style={{ fontSize: '0.9rem', fontWeight: 700, color: '#374151', marginBottom: '0.4rem' }}>Histórico de preços</h4>
      {historico.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum preço lançado para este posto.</p>}
      {historico.length > 0 && (
        <div style={{ overflowX: 'auto', maxHeight: 340, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr>
                <th style={th}>Vigência</th>
                {COMBUSTIVEIS.map(c => <th key={c.comum} style={{ ...th, textAlign: 'right' }}>{c.nome}</th>)}
                <th style={{ ...th, textAlign: 'right' }}>Desc.</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {historico.map(h => (
                <tr key={h.pradat} onClick={() => trocarData(h.pradat)} style={{ cursor: 'pointer', background: h.pradat === data ? '#eff6ff' : undefined }} title="Clique para corrigir esta data">
                  <td style={{ ...td, fontWeight: 600 }}>{dataBR(h.pradat)}</td>
                  {COMBUSTIVEIS.map(c => {
                    const com = Number(h[c.comum]) || 0, adi = c.aditivado ? Number(h[c.aditivado]) || 0 : 0
                    return (
                      <td key={c.comum} style={{ ...td, textAlign: 'right', color: com ? '#111827' : '#d1d5db' }}>
                        {com ? fmt(com) : '—'}
                        {adi > 0 && <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>ad. {fmt(adi)}</div>}
                      </td>
                    )
                  })}
                  <td style={{ ...td, textAlign: 'right' }}>{h.prades ? `${fmt(h.prades, 2)}% ${h.pratipdes === 'C' ? 'comb.' : 'todas'}` : '—'}</td>
                  <td style={td}>
                    <button className="btn btn-sm" onClick={e => { e.stopPropagation(); excluir(h.pradat) }} style={{ color: '#b91c1c' }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
